/**
 * Matriz unificada de capacidade — Responsável × Horas (Heatmap tab).
 */
import React, { useMemo, useState } from 'react';
import { LayoutGrid } from 'lucide-react';
import type { HeatmapCell, HeatmapMatrix } from '../../../services/heatmapService';
import type { OpsTeamMember } from '../../../services/operationsCenterService';
import {
  EXEC_HERO_CLASS,
  EXEC_SECTION_LABEL,
  OCCUPANCY_CELL,
  OCCUPANCY_DOT,
  OCCUPANCY_LEGEND,
  occupancyTier,
} from './opsExecutiveShared';

const WORK_HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

interface OpsCapacityHeatmapMatrixProps {
  matrix: HeatmapMatrix | undefined;
  team: OpsTeamMember[];
  /** Null quando não há FTE e produtividade persistidos. Não usar 3 × 1. */
  capacityPerHour: number | null;
  isLoading?: boolean;
  periodLabel?: string;
  /** Deixa explícito que a % não é medição contábil de capacidade. */
  occupancyCaption?: string;
  /** FTE usado na capacidade da célula. */
  configuredFte?: number;
  /** Tarefas por pessoa por hora. Distinto do limite de 8 tarefas ativas. */
  tasksPerPersonHour?: number;
}

interface MatrixCell {
  occupancy: number | null;
  count: number;
  capacity: number | null;
  tooltip: string;
}

function buildMatrix(
  cells: HeatmapCell[],
  team: OpsTeamMember[],
  capacityPerHour: number | null,
  configuredFte?: number,
  tasksPerPersonHour?: number,
): { rows: { id: string; label: string }[]; getCell: (id: string, hour: number) => MatrixCell } {
  const rows = team.length > 0
    ? team.map((member) => ({
        id: member.user_id,
        label: member.user_name?.trim() || member.user_id.slice(0, 8),
      }))
    : [...new Set(cells.flatMap((cell) => cell.topAssignees))].slice(0, 12).map((id) => ({
        id,
        label: id.slice(0, 8),
      }));

  const known = new Set(rows.map((row) => row.id));
  const counts: Record<string, Record<number, number>> = {};

  for (const cell of cells) {
    if (!WORK_HOURS.includes(cell.hour) || cell.topAssignees.length === 0) continue;
    const share = cell.count / cell.topAssignees.length;
    for (const assigneeId of cell.topAssignees) {
      if (!counts[assigneeId]) counts[assigneeId] = {};
      counts[assigneeId][cell.hour] = (counts[assigneeId][cell.hour] ?? 0) + share;
      if (!known.has(assigneeId)) {
        known.add(assigneeId);
        rows.push({ id: assigneeId, label: assigneeId.slice(0, 8) });
      }
    }
  }

  const getCell = (id: string, hour: number): MatrixCell => {
    const count = Math.round(counts[id]?.[hour] ?? 0);
    const label = rows.find((row) => row.id === id)?.label ?? id;
    const capacity = capacityPerHour != null && capacityPerHour > 0 ? capacityPerHour : null;
    const occupancy = capacity == null ? null : (count / capacity) * 100;
    return {
      occupancy,
      count,
      capacity,
      tooltip: capacity == null
        ? [
          label,
          `${String(hour).padStart(2, '0')}h · Ocupação —`,
          'Parâmetro não configurado. O padrão visual 3 FTE × 1 tarefa/pessoa/h não é capacidade real.',
          `${count} tarefas observadas`,
        ].join('\n')
        : [
          label,
          `${String(hour).padStart(2, '0')}h · Ocupação ${Math.round(occupancy ?? 0)}%`,
          'Ocupação = carga observada / capacidade configurada',
          configuredFte != null ? `FTE: ${configuredFte}` : null,
          tasksPerPersonHour != null ? `Produtividade: ${tasksPerPersonHour} tarefa/pessoa/h` : null,
          `Capacidade: ${capacity} tarefas/h`,
          `${count} tarefas observadas`,
        ].filter(Boolean).join('\n'),
    };
  };

  return { rows: rows.length > 0 ? rows : [{ id: 'equipe', label: 'Equipe' }], getCell };
}

export const OpsCapacityHeatmapMatrix: React.FC<OpsCapacityHeatmapMatrixProps> = ({
  matrix,
  team,
  capacityPerHour,
  isLoading,
  periodLabel,
  occupancyCaption,
  configuredFte,
  tasksPerPersonHour,
}) => {
  const [hoverTip, setHoverTip] = useState<string | null>(null);
  const [showNeeds, setShowNeeds] = useState(false);
  const cells = matrix?.cells ?? [];
  const hasLoad = (matrix?.totalTasks ?? 0) > 0 || cells.some((cell) => cell.count > 0);

  const grid = useMemo(
    () => buildMatrix(cells, team, capacityPerHour, configuredFte, tasksPerPersonHour),
    [cells, team, capacityPerHour, configuredFte, tasksPerPersonHour],
  );

  return (
    <div className={`${EXEC_HERO_CLASS} flex min-h-0 flex-1 flex-col`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border-default)]/50 px-5 py-3">
        <div className="flex items-center gap-2.5">
          <LayoutGrid size={18} className="text-sky-600" />
          <div>
            <p className={EXEC_SECTION_LABEL}>Heatmap de Capacidade</p>
            <p className="text-[11px] text-[var(--text-muted)]">
              Responsável × horário
              {periodLabel ? ` · ${periodLabel}` : ''}
              {occupancyCaption ? ` · ${occupancyCaption}` : ''}
              {capacityPerHour != null && configuredFte != null && tasksPerPersonHour != null
                ? ` · FTE ${configuredFte} × ${tasksPerPersonHour} tarefa/pessoa/h = ${capacityPerHour.toFixed(1)} tarefas/h`
                : ' · Ocupação — · parâmetro não configurado'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {capacityPerHour == null || capacityPerHour <= 0 ? (
            <span className="text-[9px] text-[var(--text-muted)]">
              {capacityPerHour == null
                ? 'Sem faixa de ocupação: capacidade não configurada'
                : 'Capacidade zero: ocupação percentual não se aplica'}
            </span>
          ) : OCCUPANCY_LEGEND.map((item) => (
            <div key={item.tier} className="flex items-center gap-1.5">
              <span className="text-sm leading-none">{OCCUPANCY_DOT[item.tier]}</span>
              <span className="text-[9px] text-[var(--text-muted)]">{item.label} ({item.range})</span>
            </div>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center text-[11px] text-[var(--text-muted)]">Carregando matriz…</div>
        ) : (
          <>
            {!hasLoad && (
              <div className="mb-3 rounded-sm border border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/40 px-4 py-3">
                <p className="text-[12px] font-semibold text-[var(--text-primary)]">
                  Ainda não há dados suficientes para calcular a ocupação por horário.
                </p>
                <p className="mt-1 text-[11px] text-[var(--text-secondary)]">
                  O Heatmap será preenchido conforme houver execuções com duração e responsável suficientes para estimar a ocupação.
                </p>
                <button
                  type="button"
                  onClick={() => setShowNeeds((open) => !open)}
                  className="mt-2 text-[10px] font-medium text-[var(--text-muted)] underline-offset-2 hover:text-[var(--text-primary)] hover:underline"
                >
                  Ver dados necessários
                </button>
                {showNeeds && (
                  <p className="mt-2 text-[10px] leading-relaxed text-[var(--text-muted)]">
                    A query atual usa tarefas com início no período, responsável atribuído e intervalo entre início e conclusão para cair em um horário. Sem isso a matriz fica vazia. A grade abaixo só mostra a estrutura.
                  </p>
                )}
              </div>
            )}
            <div className={!hasLoad ? 'pointer-events-none opacity-40' : undefined}>
          <table className="w-full min-w-[640px] border-separate border-spacing-1">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-[var(--bg-surface)] px-2 py-1.5 text-left text-[9px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
                  Responsável
                </th>
                {WORK_HOURS.map((h) => (
                  <th key={h} className="px-0.5 py-1 text-center text-[9px] font-bold tabular-nums text-[var(--text-muted)]">
                    {String(h).padStart(2, '0')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row) => (
                <tr key={row.id}>
                  <td className="sticky left-0 z-10 max-w-[140px] truncate bg-[var(--bg-surface)] px-2 py-1 text-[11px] font-semibold text-[var(--text-primary)]">
                    {row.label}
                  </td>
                  {WORK_HOURS.map((hour) => {
                    const cell = grid.getCell(row.id, hour);
                    const empty = cell.count <= 0;
                    const tier = cell.occupancy == null ? null : occupancyTier(cell.occupancy);
                    return (
                      <td key={hour} className="p-0">
                        <div
                          className={`flex h-9 min-w-[32px] cursor-default items-center justify-center rounded-sm text-base ${
                            empty || tier == null
                              ? 'bg-[var(--bg-elevated)] text-[11px] font-semibold text-[var(--text-secondary)]'
                              : `transition-transform hover:scale-110 hover:z-10 ${OCCUPANCY_CELL[tier]}`
                          }`}
                          title={empty ? `${row.label}\n${String(hour).padStart(2, '0')}h · sem movimento` : cell.tooltip}
                          onMouseEnter={() => setHoverTip(empty ? null : cell.tooltip)}
                          onMouseLeave={() => setHoverTip(null)}
                        >
                          {empty ? '' : tier == null ? cell.count : OCCUPANCY_DOT[tier]}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
            </div>
          </>
        )}
      </div>

      {hoverTip && (
        <div className="shrink-0 border-t border-[var(--border-default)]/40 bg-[var(--bg-elevated)]/50 px-5 py-2 text-[10px] whitespace-pre-line text-[var(--text-secondary)]">
          {hoverTip}
        </div>
      )}
    </div>
  );
};

export default OpsCapacityHeatmapMatrix;
